import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import CommunicationsInbox from '../components/CommunicationsInbox';
import { PFIFullLogo } from '../components/common/PFILogo';
import { 
  ChatBubbleLeftRightIcon,
  ArrowLeftIcon,
  HomeIcon 
} from '@heroicons/react/24/outline';

const CustomerInboxPage = () => {
  const { id: projectId } = useParams(); // Get project ID from route parameter
  const [searchParams] = useSearchParams();
  const [customer, setCustomer] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Get customer info from URL parameters (for authenticated access)
  const token = searchParams.get('token');
  const customerEmail = searchParams.get('email');
  const customerId = searchParams.get('customer');

  useEffect(() => {
    const loadCustomerAndProject = async () => {
      try {
        if (!projectId) {
          setError('Project ID is required');
          return;
        }

        // Load project details
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (!projectDoc.exists()) {
          setError('Project not found');
          return;
        }

        const projectData = { id: projectDoc.id, ...projectDoc.data() };
        
        // Set customer info - if URL params provided, use them, otherwise use project data
        const customerInfo = {
          id: customerId || 'customer',
          email: customerEmail || projectData.customerEmail || 'customer@example.com',
          name: projectData.customerName || projectData.clientName || customerEmail?.split('@')[0] || 'Customer',
        };
        
        setCustomer(customerInfo);
        setProject(projectData);

      } catch (error) {
        console.error('Error loading customer/project info:', error);
        setError('Failed to load project. Please check your link.');
      } finally {
        setLoading(false);
      }
    };

    loadCustomerAndProject();
  }, [token, customerEmail, customerId, projectId]);

  const formatProjectName = (project) => {
    if (!project) return '';
    return project.name || `Project ${project.id}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your project inbox...</p>
        </div>
      </div>
    );
  }

  if (error || !customer || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white rounded-lg shadow-md p-8">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Error</h2>
          <p className="text-gray-600 mb-4">
            {error || 'Unable to access project communications'}
          </p>
          <p className="text-sm text-gray-500">
            Please contact Pink Flamingo Interiors if you continue to have issues.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white">
        <div className="max-w-6xl mx-auto py-8 px-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <Link 
                to={`/customer/timeline/${projectId}`}
                className="inline-flex items-center px-3 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all duration-300"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Timeline
              </Link>
              <PFIFullLogo size="sm" className="text-white" />
            </div>
            <Link
              to={`/customer/project/${projectId}`}
              className="inline-flex items-center px-3 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all duration-300"
            >
              <HomeIcon className="w-4 h-4 mr-2" />
              Project Overview
            </Link>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Project Communications</h1>
              <h2 className="text-xl text-pink-100 mt-2">
                {formatProjectName(project)}
              </h2>
              <p className="text-pink-100 mt-1">Welcome back, {customer.name}</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-pink-200">
                Project Status: <span className="font-semibold text-white">
                  {project.status?.replace('_', ' ') || 'Planning'}
                </span>
              </div>
              <div className="text-sm text-pink-200 mt-1">
                {customer.email}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Your Project Messages
            </h2>
            <p className="text-gray-600 mt-1">
              All communications with Pink Flamingo Interiors about your project
            </p>
          </div>
          
          <CommunicationsInbox
            userType="customer"
            userId={customer.id}
            projectId={projectId}
            userName={customer.name}
            userEmail={customer.email}
          />
        </div>

        {/* Project Summary */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-700 mb-1">Project Name</h4>
              <p className="text-gray-600">{formatProjectName(project)}</p>
            </div>
            {project.briefDescription && (
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Description</h4>
                <p className="text-gray-600">{project.briefDescription}</p>
              </div>
            )}
            <div>
              <h4 className="font-medium text-gray-700 mb-1">Status</h4>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                {project.status?.replace('_', ' ') || 'Planning'}
              </span>
            </div>
            {project.components && project.components.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Components</h4>
                <p className="text-gray-600">{project.components.length} component(s)</p>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-8 bg-pink-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-pink-900 mb-2">Contact Pink Flamingo Interiors</h3>
          <p className="text-pink-800 mb-4">
            Need to discuss your project? Send us a message through the inbox above, 
            or contact us directly:
          </p>
          <div className="text-sm text-pink-700 space-y-1">
            <p><strong>📞 Phone:</strong> 0435 860 399</p>
            <p><strong>📧 Email:</strong> team@pinkflamingointeriors.com.au</p>
            <p><strong>🌐 Website:</strong> www.pinkflamingointeriors.com.au</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInboxPage;